import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConfirmDialog } from '../components/ConfirmDialog';
import MediaInteractionModal from '../components/MediaInteractionModal';
import api from '../utils/api';
import './Requests.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const getMediaUrl = (key: string | null | undefined): string | null => {
  if (!key) return null;
  if (key.startsWith('http')) return key;
  return `${BACKEND_URL}/uploads/${key}`;
};

type MainTabType       = 'role-requests' | 'connection-requests';
type RoleTabType       = 'received' | 'sent';
type ConnectionTabType = 'received' | 'sent';

interface RoleRequest {
  id: number;
  story_title: string;
  role_type: string;
  role_name: string;
  role_description?: string;
  status: string;
  sender_name?: string;
  sender_email?: string;
  sender_id?: number;
  sender_dp?: string | null;
  receiver_name?: string;
  receiver_email?: string;
  receiver_id?: number;
  receiver_dp?: string | null;
  created_at: string;
}

interface ConnectionRequest {
  id: number;
  user_id_1: number;
  user_id_2: number;
  status: string;
  initiated_by: number;
  other_user_name: string;
  other_user_email: string;
  other_user_id: number;
  other_user_dp?: string | null;
  created_at: string;
}

interface MediaItem {
  id: number;
  file_name: string;
  media_type: string;
  is_collab?: boolean;
  collaborator_role?: string | null;
  owner_name?: string | null;
}

interface Collaborator {
  id: number;
  media_id: number;
  role_name: string;
  user_id: number;
  user_name: string;
  user_dp: string | null;
}

interface UserProfile {
  user: {
    id: number;
    name: string;
    email: string;
    city?: string;
    state?: string;
    roles?: string[];
    profile_picture?: string | null;
  };
  connectionStatus: string | null;
  connectionInitiatedBy: number | null;
  connectionCount?: number;
  followerCount?: number;
  photos: MediaItem[];
  videos: MediaItem[];
}

/* ── Small avatar component with its own error state ── */
const Avatar: React.FC<{
  dpKey?: string | null;
  name: string;
  className: string;
  onClick?: () => void;
}> = ({ dpKey, name, className, onClick }) => {
  const [err, setErr] = useState(false);
  const url = getMediaUrl(dpKey);
  return (
    <div className={className} onClick={onClick}>
      {url && !err
        ? <img src={url} alt={name} onError={() => setErr(true)} />
        : name.charAt(0).toUpperCase()
      }
    </div>
  );
};

/* ── Lightbox rendered via portal ── */
/* ============================================================
   Page
   ============================================================ */

const RequestsPage: React.FC = () => {
  const navigate = useNavigate();
  const { confirm, confirmDialog } = useConfirmDialog();
  const [mainTab,       setMainTab]       = useState<MainTabType>('role-requests');
  const [roleTab,       setRoleTab]       = useState<RoleTabType>('received');
  const [connectionTab, setConnectionTab] = useState<ConnectionTabType>('received');

  const [receivedRoleRequests,    setReceivedRoleRequests]    = useState<RoleRequest[]>([]);
  const [sentRoleRequests,        setSentRoleRequests]        = useState<RoleRequest[]>([]);
  const [receivedConnRequests,    setReceivedConnRequests]    = useState<ConnectionRequest[]>([]);
  const [sentConnRequests,        setSentConnRequests]        = useState<ConnectionRequest[]>([]);

  const [loading, setLoading]   = useState(true);
  const [error,   setError]     = useState('');
  const [message, setMessage]   = useState('');
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  useEffect(() => { loadAllRequests(); }, []);

  const loadAllRequests = async () => {
    setLoading(true);
    try {
      const [roleReceivedRes, roleSentRes, connReceivedRes, connSentRes] = await Promise.all([
        api.get('/requests/received'),
        api.get('/requests/sent'),
        api.get('/connections/requests'),
        api.get('/connections/requests/sent'),
      ]);
      setReceivedRoleRequests(roleReceivedRes.data.requests);
      setSentRoleRequests(roleSentRes.data.requests);
      setReceivedConnRequests(connReceivedRes.data.requests);
      setSentConnRequests(connSentRes.data.requests);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptRoleRequest = async (requestId: number) => {
    try {
      await api.post(`/requests/${requestId}/accept`);
      setMessage('Role request accepted!');
      setTimeout(() => setMessage(''), 2000);
      loadAllRequests();
    } catch (err: any) { setError(err.response?.data?.message || 'Failed to accept request'); }
  };

  const handleRejectRoleRequest = async (requestId: number) => {
    const approved = await confirm({
      title: 'Reject Role Request',
      message: 'Reject this role request?',
      confirmLabel: 'Reject',
      cancelLabel: 'Keep Pending',
      danger: true,
    });
    if (!approved) return;
    try {
      await api.post(`/requests/${requestId}/reject`);
      setMessage('Role request rejected');
      setTimeout(() => setMessage(''), 2000);
      loadAllRequests();
    } catch (err: any) { setError(err.response?.data?.message || 'Failed to reject request'); }
  };

  const handleAcceptConnection = async (requestId: number) => {
    try {
      await api.post(`/connections/${requestId}/accept`);
      setMessage('Connection accepted!');
      setTimeout(() => setMessage(''), 2000);
      loadAllRequests();
    } catch (err: any) { setError(err.response?.data?.message || 'Failed to accept connection'); }
  };

  const handleRejectConnection = async (requestId: number) => {
    const approved = await confirm({
      title: 'Reject Connection Request',
      message: 'Reject this connection request?',
      confirmLabel: 'Reject',
      cancelLabel: 'Keep Pending',
      danger: true,
    });
    if (!approved) return;
    try {
      await api.post(`/connections/${requestId}/reject`);
      setMessage('Connection rejected');
      setTimeout(() => setMessage(''), 2000);
      loadAllRequests();
    } catch (err: any) { setError(err.response?.data?.message || 'Failed to reject connection'); }
  };

  const handleChatWithSender = async (senderId: number, storyTitle: string, roleName: string) => {
    try {
      const res = await api.get(`/connections/check/${senderId}`);
      if (res.data.isConnected) {
        navigate(`/direct-messages/${senderId}`, {
          state: { initialMessage: `Regarding role request for "${roleName}" in "${storyTitle}"` },
        });
      } else {
        setError('You need to be connected to chat with this user');
      }
    } catch (err: any) { setError(err.response?.data?.message || 'Failed to start chat'); }
  };

  const handleConnect = async (userId: number) => {
    try {
      await api.post('/connections/request', { targetUserId: userId });
      setMessage('Connection request sent!');
      setTimeout(() => setMessage(''), 2000);
      setSelectedUserId(null);
      loadAllRequests();
    } catch (err: any) { setError(err.response?.data?.message || 'Failed to send request'); }
  };

  const getRoleTypeIcon = (rt: string) =>
    rt === 'character' ? '🎭' : rt === 'crew' ? '🎬' : rt === 'extra' ? '👥' : '📋';

  const getStatusBadge = (status: string) => {
    const map: Record<string, { class: string; text: string }> = {
      pending:  { class: 'status-pending',  text: 'Pending'  },
      accepted: { class: 'status-accepted', text: 'Accepted' },
      rejected: { class: 'status-rejected', text: 'Rejected' },
    };
    return map[status] || map.pending;
  };

  // Count only pending received connection requests for the badge
  const pendingConnReceived = receivedConnRequests.filter(r => r.status === 'pending').length;
  const totalConnRequests   = receivedConnRequests.length + sentConnRequests.length;

  if (loading) return <div className="requests-container"><div className="loading">Loading requests…</div></div>;

  return (
    <div className="requests-container">
      {confirmDialog}

      <div className="requests-header">
        <button onClick={() => navigate('/content')} className="back-btn">← Back</button>
        <h1>Requests</h1>
      </div>

      {error   && <div className="error-message">{error}</div>}
      {message && <div className="success-message">{message}</div>}

      {/* ── Main tabs ── */}
      <div className="main-tabs">
        <button
          className={`main-tab ${mainTab === 'role-requests' ? 'active' : ''}`}
          onClick={() => setMainTab('role-requests')}
        >
          Role Requests ({receivedRoleRequests.length + sentRoleRequests.length})
        </button>
        <button
          className={`main-tab ${mainTab === 'connection-requests' ? 'active' : ''}`}
          onClick={() => setMainTab('connection-requests')}
        >
          Connection Requests
          {pendingConnReceived > 0 && (
            <span className="tab-pending-badge">{pendingConnReceived}</span>
          )}
        </button>
      </div>

      {/* ══════════════════════════════════════════
          ROLE REQUESTS
          ══════════════════════════════════════════ */}
      {mainTab === 'role-requests' && (
        <>
          <div className="requests-tabs">
            <button
              className={`tab ${roleTab === 'received' ? 'active' : ''}`}
              onClick={() => setRoleTab('received')}
            >
              Received ({receivedRoleRequests.length})
            </button>
            <button
              className={`tab ${roleTab === 'sent' ? 'active' : ''}`}
              onClick={() => setRoleTab('sent')}
            >
              Sent ({sentRoleRequests.length})
            </button>
          </div>

          <div className="requests-content">
            <div className="requests-list">
              {roleTab === 'received' ? (
                receivedRoleRequests.length === 0 ? (
                  <div className="no-requests"><p>No pending role requests</p></div>
                ) : (
                  receivedRoleRequests.map((req) => (
                    <div key={req.id} className="request-card">
                      <div className="request-icon">{getRoleTypeIcon(req.role_type)}</div>
                      {req.sender_id && (
                        <Avatar
                          dpKey={req.sender_dp}
                          name={req.sender_name || '?'}
                          className="request-user-avatar"
                          onClick={() => setSelectedUserId(req.sender_id!)}
                        />
                      )}
                      <div className="request-details">
                        <h3>{req.story_title}</h3>
                        <p className="request-from">
                          From:{' '}
                          <strong
                            className="sender-name-link"
                            onClick={() => req.sender_id && setSelectedUserId(req.sender_id)}
                          >
                            {req.sender_name}
                          </strong>
                          ({req.sender_email})
                        </p>
                        <div className="request-role">
                          <span className="role-label">Role</span>
                          <span className="role-value">{req.role_name}</span>
                          <span className={`role-type-badge ${req.role_type}`}>{req.role_type}</span>
                        </div>
                        {req.role_description && <p className="request-description">{req.role_description}</p>}
                        <p className="request-date">{new Date(req.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="request-actions">
                        <button onClick={() => handleAcceptRoleRequest(req.id)} className="accept-btn">Accept</button>
                        <button onClick={() => handleRejectRoleRequest(req.id)} className="reject-btn">Reject</button>
                        {req.sender_id && (
                          <button
                            onClick={() => handleChatWithSender(req.sender_id!, req.story_title, req.role_name)}
                            className="chat-request-btn"
                          >
                            Chat
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )
              ) : (
                sentRoleRequests.length === 0 ? (
                  <div className="no-requests"><p>No sent role requests</p></div>
                ) : (
                  sentRoleRequests.map((req) => (
                    <div key={req.id} className="request-card">
                      <div className="request-icon">{getRoleTypeIcon(req.role_type)}</div>
                      {req.receiver_id && (
                        <Avatar
                          dpKey={req.receiver_dp}
                          name={req.receiver_name || '?'}
                          className="request-user-avatar"
                          onClick={() => setSelectedUserId(req.receiver_id!)}
                        />
                      )}
                      <div className="request-details">
                        <h3>{req.story_title}</h3>
                        <p className="request-from">
                          To:{' '}
                          <strong
                            className="sender-name-link"
                            onClick={() => req.receiver_id && setSelectedUserId(req.receiver_id)}
                          >
                            {req.receiver_name}
                          </strong>
                          ({req.receiver_email})
                        </p>
                        <div className="request-role">
                          <span className="role-label">Role</span>
                          <span className="role-value">{req.role_name}</span>
                          <span className={`role-type-badge ${req.role_type}`}>{req.role_type}</span>
                        </div>
                        {req.role_description && <p className="request-description">{req.role_description}</p>}
                        <p className="request-date">{new Date(req.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="request-status">
                        <span className={`status-badge ${getStatusBadge(req.status).class}`}>
                          {getStatusBadge(req.status).text}
                        </span>
                      </div>
                    </div>
                  ))
                )
              )}
            </div>
          </div>
        </>
      )}

      {/* ══════════════════════════════════════════
          CONNECTION REQUESTS
          ══════════════════════════════════════════ */}
      {mainTab === 'connection-requests' && (
        <>
          <div className="requests-tabs">
            <button
              className={`tab ${connectionTab === 'received' ? 'active' : ''}`}
              onClick={() => setConnectionTab('received')}
            >
              Received ({receivedConnRequests.length})
              {pendingConnReceived > 0 && (
                <span className="tab-pending-badge">{pendingConnReceived}</span>
              )}
            </button>
            <button
              className={`tab ${connectionTab === 'sent' ? 'active' : ''}`}
              onClick={() => setConnectionTab('sent')}
            >
              Sent ({sentConnRequests.length})
            </button>
          </div>

          <div className="requests-content">
            <div className="requests-list">

              {/* ── Received connection requests ── */}
              {connectionTab === 'received' && (
                receivedConnRequests.length === 0 ? (
                  <div className="no-requests">
                    <p>No connection requests received</p>
                    <button onClick={() => navigate('/user-search')} className="find-people-btn-inline">
                      Find People to Connect
                    </button>
                  </div>
                ) : (
                  receivedConnRequests.map((req) => (
                    <div key={req.id} className="connection-request-card">
                      <Avatar
                        dpKey={req.other_user_dp}
                        name={req.other_user_name}
                        className="connection-avatar"
                        onClick={() => setSelectedUserId(req.other_user_id)}
                      />
                      <div className="connection-details">
                        <h3
                          className="connection-name-link"
                          onClick={() => setSelectedUserId(req.other_user_id)}
                        >
                          {req.other_user_name}
                        </h3>
                        <p className="connection-email">{req.other_user_email}</p>
                        <p className="connection-date">
                          Requested {new Date(req.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="connection-actions">
                        {req.status === 'pending' ? (
                          <>
                            <button onClick={() => handleAcceptConnection(req.id)} className="accept-btn">Accept</button>
                            <button onClick={() => handleRejectConnection(req.id)} className="reject-btn">Reject</button>
                          </>
                        ) : (
                          <span className={`status-badge ${getStatusBadge(req.status).class}`}>
                            {getStatusBadge(req.status).text}
                          </span>
                        )}
                        <button
                          onClick={() => setSelectedUserId(req.other_user_id)}
                          className="view-profile-btn-small"
                        >
                          View Profile
                        </button>
                      </div>
                    </div>
                  ))
                )
              )}

              {/* ── Sent connection requests ── */}
              {connectionTab === 'sent' && (
                sentConnRequests.length === 0 ? (
                  <div className="no-requests">
                    <p>No sent connection requests</p>
                    <button onClick={() => navigate('/user-search')} className="find-people-btn-inline">
                      Find People to Connect
                    </button>
                  </div>
                ) : (
                  sentConnRequests.map((req) => (
                    <div key={req.id} className="connection-request-card">
                      <Avatar
                        dpKey={req.other_user_dp}
                        name={req.other_user_name}
                        className="connection-avatar"
                        onClick={() => setSelectedUserId(req.other_user_id)}
                      />
                      <div className="connection-details">
                        <h3
                          className="connection-name-link"
                          onClick={() => setSelectedUserId(req.other_user_id)}
                        >
                          {req.other_user_name}
                        </h3>
                        <p className="connection-email">{req.other_user_email}</p>
                        <p className="connection-date">
                          Sent {new Date(req.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="request-status">
                        <span className={`status-badge ${getStatusBadge(req.status).class}`}>
                          {getStatusBadge(req.status).text}
                        </span>
                      </div>
                    </div>
                  ))
                )
              )}

            </div>
          </div>
        </>
      )}

      {selectedUserId && (
        <UserProfileModal
          userId={selectedUserId}
          onClose={() => setSelectedUserId(null)}
          onConnect={handleConnect}
          onDisconnect={async (userId) => {
            try {
              await api.delete(`/connections/${userId}`);
              setMessage('Disconnected successfully!');
              setTimeout(() => setMessage(''), 2000);
              await loadAllRequests();
            } catch (err: any) {
              setError(err.response?.data?.message || 'Failed to disconnect');
              throw err;
            }
          }}
          onStartChat={(uid) => { setSelectedUserId(null); navigate(`/direct-messages/${uid}`); }}
        />
      )}
    </div>
  );
};

/* ============================================================
   User Profile Modal
   ============================================================ */

interface UserProfileModalProps {
  userId: number;
  onClose: () => void;
  onConnect: (userId: number) => void | Promise<void>;
  onDisconnect: (userId: number) => void | Promise<void>;
  onStartChat: (userId: number) => void;
}

const UserProfileModal: React.FC<UserProfileModalProps> = ({
  userId,
  onClose,
  onConnect,
  onDisconnect,
  onStartChat,
}) => {
  const [profile,        setProfile]        = useState<UserProfile | null>(null);
  const [loading,        setLoading]        = useState(true);
  const [dpError,        setDpError]        = useState(false);
  const [activeMediaTab, setActiveMediaTab] = useState<'photos' | 'videos'>('photos');
  const [disconnecting,  setDisconnecting]  = useState(false);

  const [lightbox,      setLightbox]      = useState<{ url: string; isVideo: boolean; mediaId: number } | null>(null);
  const [collabMap,     setCollabMap]     = useState<Record<number, Collaborator[]>>({});
  const [collabLoading, setCollabLoading] = useState<Record<number, boolean>>({});

  useEffect(() => {
    setLoading(true);
    setDpError(false);
    setCollabMap({});
    api.get(`/user/${userId}/profile`)
      .then(res => setProfile(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { if (lightbox) setLightbox(null); else onClose(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, onClose]);

  const fetchCollabs = useCallback(async (mediaId: number) => {
    if (collabMap[mediaId] !== undefined) return;
    setCollabLoading(prev => ({ ...prev, [mediaId]: true }));
    try {
      const res = await api.get(`/media/${mediaId}/collaborators`);
      setCollabMap(prev => ({ ...prev, [mediaId]: res.data.collaborators }));
    } catch {
      setCollabMap(prev => ({ ...prev, [mediaId]: [] }));
    } finally {
      setCollabLoading(prev => ({ ...prev, [mediaId]: false }));
    }
  }, [collabMap]);

  const openLightbox = (url: string, isVideo: boolean, mediaId: number) => {
    setLightbox({ url, isVideo, mediaId });
    fetchCollabs(mediaId);
  };

  const handleDisconnect = useCallback(async () => {
    setDisconnecting(true);
    try {
      await onDisconnect(userId);
      setProfile((current) =>
        current
          ? {
              ...current,
              connectionStatus: null,
              connectionInitiatedBy: null,
            }
          : current,
      );
    } finally {
      setDisconnecting(false);
    }
  }, [onDisconnect, userId]);

  if (loading || !profile) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content-large" onClick={e => e.stopPropagation()}>
          <div className="loading" style={{ minHeight: 'unset', padding: '48px 0' }}>Loading…</div>
        </div>
      </div>
    );
  }

  const dpUrl      = getMediaUrl(profile.user.profile_picture);
  const showDp     = dpUrl && !dpError;
  const photos     = profile.photos || [];
  const videos     = profile.videos || [];
  const activeMedia = activeMediaTab === 'photos' ? photos : videos;

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content-large" onClick={e => e.stopPropagation()}>
          <button onClick={onClose} className="modal-close">✕</button>

          <div className="profile-header">
            <div
              className="profile-avatar-large"
              style={{ cursor: showDp ? 'zoom-in' : 'default' }}
              onClick={() => showDp && openLightbox(dpUrl!, false, -1)}
            >
              {showDp
                ? <img src={dpUrl!} alt={profile.user.name} onError={() => setDpError(true)} />
                : profile.user.name.charAt(0).toUpperCase()
              }
            </div>
            <h2>{profile.user.name}</h2>
            <p className="profile-email">{profile.user.email}</p>
            <div className="profile-modal-stats">
              <span><strong>{profile.connectionCount ?? 0}</strong> Connections</span>
              <span><strong>{profile.followerCount ?? 0}</strong> Followers</span>
            </div>
          </div>

          <div className="profile-details">
            {profile.user.roles && profile.user.roles.length > 0 && (
              <div className="profile-detail-item">
                <span className="detail-label">Roles</span>
                <div className="detail-value" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {profile.user.roles.map((role, i) => (
                    <span key={i} className="role-type-badge">{role}</span>
                  ))}
                </div>
              </div>
            )}
            {profile.user.city && (
              <div className="profile-detail-item">
                <span className="detail-label">Location</span>
                <span className="detail-value">{profile.user.city}, {profile.user.state}</span>
              </div>
            )}
          </div>

          {(photos.length > 0 || videos.length > 0) && (
            <div style={{ marginTop: '28px', paddingTop: '24px', borderTop: '1px solid var(--rule)' }}>
              <div className="media-tabs-modal">
                <button
                  className={`media-tab-modal ${activeMediaTab === 'photos' ? 'active' : ''}`}
                  onClick={() => setActiveMediaTab('photos')}
                >
                  Photos ({photos.length})
                </button>
                <button
                  className={`media-tab-modal ${activeMediaTab === 'videos' ? 'active' : ''}`}
                  onClick={() => setActiveMediaTab('videos')}
                >
                  Videos ({videos.length})
                </button>
              </div>
              <div className="media-gallery-modal">
                {activeMedia.length > 0 ? (
                  <div className="media-grid-modal">
                    {activeMedia.map((item) => {
                      const url = getMediaUrl(item.file_name);
                      if (!url) return null;
                      const isVideo = item.media_type === 'video';
                      return (
                        <div
                          key={item.id}
                          className={`media-item-modal${isVideo ? ' video-item-modal' : ''}`}
                          onClick={() => openLightbox(url, isVideo, item.id)}
                          style={{ cursor: 'zoom-in' }}
                        >
                          {isVideo
                            ? <><video src={url} preload="metadata" /><span className="us-play-icon">▶</span></>
                            : <img src={url} alt="Photo" onError={e => { e.currentTarget.parentElement!.style.display = 'none'; }} />
                          }
                          {item.is_collab && (
                            <span className="profile-collab-media-badge">
                              Tagged as {item.collaborator_role || 'Collaborator'}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="no-media-modal"><p>No {activeMediaTab} yet.</p></div>
                )}
              </div>
            </div>
          )}

          <div className="profile-actions">
            {!profile.connectionStatus && (
              <button onClick={() => { onConnect(userId); onClose(); }} className="connect-btn-large">Connect</button>
            )}
            {profile.connectionStatus === 'pending' && (
              <span className="status-badge-large">Request Pending</span>
            )}
            {profile.connectionStatus === 'accepted' && (
              <>
                <button onClick={() => { onStartChat(userId); onClose(); }} className="chat-btn-large">Chat</button>
                <button
                  onClick={() => void handleDisconnect()}
                  className="disconnect-btn-large"
                  disabled={disconnecting}
                >
                  {disconnecting ? 'Disconnecting...' : 'Disconnect'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {lightbox && (
        <MediaInteractionModal
          mediaId={lightbox.mediaId}
          src={lightbox.url}
          isVideo={lightbox.isVideo}
          collaborators={(collabMap[lightbox.mediaId] || []).map(c => ({
            ...c,
            user_dp: getMediaUrl(c.user_dp),
          }))}
          collaboratorsLoading={Boolean(collabLoading[lightbox.mediaId])}
          onClose={() => setLightbox(null)}
        />
      )}
    </>
  );
};

export default RequestsPage;
